<script lang="ts" setup>
import { computed } from 'vue'
import { PhStar } from '@phosphor-icons/vue'
import { cva, type VariantProps } from 'class-variance-authority'

import { formatNumber } from '../utils'

const starRatingContainerVariants = cva('',
  {
    variants: {
      size: {
        sm: 'w-[5.625rem] h-4.5',
        md: 'w-[7.5rem] h-6',
        lg: 'w-[10rem] h-8'
      }
    }
  }
)

const starRatingTextVariants = cva('',
  {
    variants: {
      size: {
        sm: 'text-heading-2xs',
        md: 'text-heading-xs',
        lg: 'text-heading-xs'
      }
    }
  }
)

const totalTextVariants = cva('',
  {
    variants: {
      size: {
        sm: 'text-heading-3xs',
        md: 'text-heading-2xs',
        lg: 'text-heading-2xs'
      }
    }
  }
)

const starSizeVariants = cva('',
  {
    variants: {
      size: {
        sm: 'w-4.5 h-4.5',
        md: 'w-6 h-6',
        lg: 'w-8 h-8'
      }
    }
  }
)

const overlayHeightVariants = cva('absolute overflow-hidden',
  {
    variants: {
      size: {
        sm: 'h-4.5',
        md: 'h-6',
        lg: 'h-8'
      }
    }
  }
)

type StarRatingProps = VariantProps<typeof starRatingContainerVariants>

const props = withDefaults(
  defineProps<{
    value: number,
    total: number,
    size: StarRatingProps['size'],
    minimumFractionDigits?: number,
    maximumFractionDigits?: number,
    singleStar?: boolean
  }>(),
  {
    total: 0,
    size: 'sm',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    singleStar: false
  }
)

const starWidth = computed(() => {
  switch (props.size) {
    case 'md':
      return 24
    case 'lg':
      return 32
    default:
      return 18
  }
})

const STAR_STEP = 0.25

const partialFillWidth = computed(() => {
  if (props.singleStar) {
    return null
  }
  const partialFill = props.value / props.total
  const stepWidth = starWidth.value * STAR_STEP
  const steps = Math.floor(starWidth.value * 5 * partialFill / stepWidth)

  return steps * stepWidth
})

const formattedStarRating = computed(() => {
  return formatNumber(props.value, props.minimumFractionDigits, props.maximumFractionDigits)
})
</script>

<template>
  <div class="flex flex-row items-end gap-1 leading-none my-1">
    <div v-if="singleStar">
      <span :class="starSizeVariants({size})">
        <PhStar :size="starWidth"
                weight="fill"
                class="text-rating-foreground" />
      </span>
    </div>
    <div v-else
         :class="starRatingContainerVariants({size})">
      <div class="absolute"
           :class="starRatingContainerVariants({size})">
        <span v-for="(star, index) in 5"
              :key="index"
              class="relative inline-block"
              :class="starSizeVariants({size})">
          <PhStar :size="starWidth"
                  weight="fill"
                  class="text-rating-background" />
        </span>
      </div>
      <div :class="overlayHeightVariants({size})"
           :style="{width: partialFillWidth + 'px'}">
        <div :class="starRatingContainerVariants({size})">
          <span v-for="(star, index) in 5"
                :key="index"
                class="relative inline-block"
                :class="starSizeVariants({size})">
            <PhStar :size="starWidth"
                    weight="fill"
                    class="text-rating-foreground" />
          </span>
        </div>
      </div>
    </div>
    <div class="font-heading text-aside-foreground">
      <span :class="starRatingTextVariants({size})">
        {{ formattedStarRating }}
      </span>
      <span v-if="total && total > 0"
            :class="totalTextVariants({size})">
        /{{ total }}
      </span>
    </div>
  </div>
</template>
